import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [preferences, setPreferences] = useState({
    sales_dips: true,
    reports: true,
    new_sales: true,
    team_changes: true,
  });

  const checkExistingSubscription = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch (err) { console.error("Hook error:", err.message);
      setIsSubscribed(false);
    }
  }, []);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
      checkExistingSubscription();
    }
  }, [checkExistingSubscription]);

  const fetchPreferences = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/push/preferences`, { withCredentials: true });
      setPreferences(data);
    } catch (err) { console.error("Hook error:", err.message); }
  }, []);

  const updatePreference = useCallback(async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    try {
      await axios.patch(`${BACKEND_URL}/api/push/preferences`, { [key]: value }, { withCredentials: true });
    } catch (err) {
      console.error('Push preference update error:', err.message);
      // Rollback on failure
      setPreferences(prev => ({ ...prev, [key]: !value }));
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!isSupported || !VAPID_PUBLIC_KEY) return false;
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      await axios.post(`${BACKEND_URL}/api/push/subscribe`, { subscription: sub.toJSON() }, { withCredentials: true });
      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscribe error:', err);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${BACKEND_URL}/api/push/unsubscribe`, {
          data: { endpoint: sub.endpoint },
          withCredentials: true,
        });
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
      return true;
    } catch (err) { console.error("Hook error:", err.message);
      return false;
    }
  }, []);

  const sendTest = useCallback(async () => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/push/test`, {}, { withCredentials: true });
      return data;
    } catch (err) { console.error("Hook error:", err.message);
      return null;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    preferences,
    subscribe,
    unsubscribe,
    fetchPreferences,
    updatePreference,
    sendTest,
  };
}
